import React from 'react';
import { Building2, Eye, Archive } from 'lucide-react';
import { getPropertyStatus } from '../../utils/propertyHelpers';
import { PROPERTY_ICONS } from '../../constants/propertyIcons';

/**
 * Componente para mostrar las tarjetas de estadísticas generales
 * 
 * MODOS:
 * - Activas (showArchived=false): 4 tarjetas (Total, Próximas, Abiertas, Finalizadas)
 * - Archivadas (showArchived=true): 3 tarjetas (Total, Activas, Archivadas)
 * 
 * @param {object} stats - Estadísticas generales { totalProperties, ... }
 * @param {array} properties - Lista de propiedades activas
 * @param {boolean} showArchived - Si está en modo archivadas
 * @param {number} archivedCount - Número de propiedades archivadas
 */
const StatsCards = ({ 
  stats, 
  properties = [], 
  showArchived = false,
  archivedCount = 0 
}) => {
  let cards;

  if (showArchived) {
    // Modo archivadas: 3 tarjetas
    const totalProperties = properties.length + archivedCount;
    
    cards = [
      {
        icon: Building2,
        color: 'text-amber-400',
        bgColor: 'bg-amber-400/10',
        label: 'Total',
        value: totalProperties
      },
      {
        icon: Eye,
        color: 'text-green-400',
        bgColor: 'bg-green-400/10',
        label: 'Activas',
        value: properties.length
      },
      {
        icon: Archive,
        color: 'text-slate-400',
        bgColor: 'bg-slate-400/10',
        label: 'Archivadas',
        value: archivedCount
      }
    ];
  } else {
    // Modo activas: 5 tarjetas (incluye Oferta Ciega y Última Oportunidad)
    const propertiesStats = properties.reduce((acc, property) => {
      const status = getPropertyStatus(property);
      if (status === 'UPCOMING') acc.upcoming++;
      else if (status === 'BLIND_WAITING' || status === 'BLIND_ACTIVE') acc.blind++;
      else if (status === 'OPEN') acc.open++;
      else if (status === 'LAST_CHANCE') acc.lastChance++;
      else if (status === 'CLOSED') acc.closed++;
      return acc;
    }, { upcoming: 0, blind: 0, open: 0, lastChance: 0, closed: 0 });

    // Combinar OPEN + LAST_CHANCE en una sola tarjeta "Abiertas"
    const openTotal = propertiesStats.open + propertiesStats.lastChance;

    cards = [
      {
        ...PROPERTY_ICONS.TOTAL,
        value: stats.totalProperties
      },
      {
        ...PROPERTY_ICONS.UPCOMING,
        value: propertiesStats.upcoming
      },
      {
        ...PROPERTY_ICONS.BLIND_WAITING,
        label: 'Oferta Ciega',
        value: propertiesStats.blind
      },
      {
        ...PROPERTY_ICONS.OPEN,
        value: openTotal
      },
      {
        ...PROPERTY_ICONS.CLOSED,
        value: propertiesStats.closed
      }
    ];
  }

  // Grid: 3 columnas para archivadas, 4 para activas
  const gridCols = showArchived 
    ? 'grid-cols-3' 
    : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5';

  return (
    <div className={`grid ${gridCols} gap-3 mb-8`}>
      {cards.map((card, index) => {
        const Icon = card.icon;
        return (
          <div
            key={index}
            className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all min-h-[66px]"
          >
            {/* Layout horizontal: icono y label a la izquierda, valor a la derecha */}
            <div className="flex items-center justify-between gap-3">
              {/* Icono y label */}
              <div className="flex items-center gap-2 min-w-0">
                <div className={`${card.bgColor} p-2 rounded-lg flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${card.color}`} />
                </div>
                <span className="text-slate-400 text-xs font-medium truncate">
                  {card.label}
                </span>
              </div>
              
              {/* Valor */}
              <div className="text-2xl font-bold text-white flex-shrink-0">
                {card.value}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;